import { TableActionIconButton } from "_shared/_views/tables/table-toolbar";
import { TableActionLoadingButton } from "_shared/_views/tables/table-toolbar";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import LabelIcon from "@mui/icons-material/LabelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import UploadIcon from "@mui/icons-material/Upload";
import { Theme } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import Button from "@mui/material/Button";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import DocumentSubjectMatters from "documents/classification/_views/document-subject-matters";
import { DocumentDownloadName, documentDownloadRowAction } from "documents/downloads/_views/document-download-control";
import EditSubjectMattersDialog from "documents/classification/_views/edit-subject-matters-dialog";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";

import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
} from "_shared/_errors/api-error-message";
import {
  useArchiveDocumentMutation,
  useCreateDocumentsMutation,
  useDeleteDocumentMutation,
  useDocumentListCounts,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
  useUnarchiveDocumentMutation,
} from "documents/_api/document-api";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import { DocumentCategory, documentCategoriesOf } from "documents/categories/document-category";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";
import { documentUsagesText } from "documents/usages/document-usages";
import { filesize } from "filesize";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_Updater,
  type MRT_TableInstance,
} from "material-react-table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import type { TableViewSpec } from "_shared/_views/list-pages/table-view-params";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import { useTableViewParams } from "_shared/_views/list-pages/use-table-view-params";
import { ColumnFilterChips } from "_shared/_views/list-pages/column-filter-chips";
import ListChipRail from "_shared/_views/list-pages/list-chip-rail";
import ListChips from "_shared/_views/list-pages/list-chips";
import type { ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageTabs,
  ListPageToolbarButtons,
  type ListPageTab,
} from "_shared/_views/list-pages/list-page-heading";
import {
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";

import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { HttpStatusCode } from "axios";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import {
  getDocumentsQueryByInterface,
  updateDocumentsInterfaceQuery,
} from "documents/_stores/documents-redux-slice";
import {
  CATEGORY_PARAMETER,
  chosenCategories,
  documentChipCounts,
  documentChipGroups,
  documentListFilter,
  documentListSubject,
  DOCUMENTS_CURRENT_VIEW,
  DocumentsTab,
  documentsTabPath,
  listsArchive,
  OWNER_PARAMETER,
  readDocumentsTab,
  rowsMixCategories,
} from "documents/_views/pages/document-list-chips";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import { cleanupDeletedDocumentReferences as cleanupProposalDeletedDocumentReferences } from "work/proposals/_store/proposals-redux-slice";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import { cleanupDeletedDocumentReferences as cleanupWorkAgreementDeletedDocumentReferences } from "work/work-agreements/_store/work-agreement-redux-slice";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { useSelector } from "react-redux";
import { DocumentFileType } from "documents/document-file-type";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import { recordPage } from "permissions/_views/wording/permission-wording";
import Loader from "_shared/_views/loading/loader";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { TimestampCell } from "_shared/_views/tables/date-cells";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { usePersistedColumnVisibility } from "_shared/_views/tables/table-column-visibility-preference";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { ViewsMenu } from "_shared/_views/list-pages/views-menu";

/**
 * The weight and the size the document's name has always had. The link inside it brings its own
 * color, its underline and its ellipsis — see `row-link`.
 */
const NameText = styled("span")({
  // Its lines are the grid's own rows, so the icon can be centered on the first two of them.
  display: "contents",
  fontSize: "1em",
  fontWeight: "500",
  "& > *": { gridColumn: 2, minWidth: 0 },
});
// The icon is centered on the name and the line under it -- the processing state or the subject
// matter -- which is how a reader takes them in. Centered on the whole block it fell below the name
// on a document with more lines, and at the top of it it stood above the pair (#6720).
const NameContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "grid",
  gridTemplateColumns: "24px minmax(0, 1fr)",
  minWidth: 0,
  rowGap: theme.spacing(0.25),
  "& > svg": {
    gridColumn: 1,
    gridRow: "1 / span 2",
    width: "24px",
  },
}));
// The subject matter is read with the name, not across the row from it (#6720).
const SubjectMatterLine = styled("div")({
  fontWeight: 400,
  minWidth: 0,
});

/**
 * The category rail and the list on one surface, on a pane wide enough for both (#6720). It is the
 * table's own paper -- its elevation and the pane's full height -- and the table inside it lies flat,
 * so the rail reads as part of the list rather than a column beside it.
 */
const RailAndList = styled(Paper)({
  display: "flex",
  height: "100%",
  overflow: "hidden",
  "& > .MuiPaper-root": { boxShadow: "none", flex: "1 1 auto", minWidth: 0 },
});
const UploadingLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));
const AlertActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const columnHelper = createMRTColumnHelper<Document | UnavailableDocument>();

// The field `GET documents` orders by for each column the table lets you sort. A column id is not
// an order field, so matching ids against the enum's values sent a sort as the default, Created
// (#5454). A sortable column missing from this map falls back to Created the same way.
const orderFieldByColumnId: Readonly<Partial<Record<string, DocumentOrderField>>> = {
  name: DocumentOrderField.Name,
  created: DocumentOrderField.Created,
  fileSize: DocumentOrderField.Size,
};

/**
 * What the URL keeps of each tab (#6712): its chips, the name filter under the parameter the API
 * takes, the sort, and the page size. Declared per tab because the tabs' category chips differ.
 */
const DOCUMENTS_VIEW: Readonly<Record<DocumentsTab, TableViewSpec>> = {
  [DocumentsTab.Documents]: {
    chipGroups: documentChipGroups(DocumentsTab.Documents),
    columnFilterParameters: { name: "name" },
    sortableColumnIds: Object.keys(orderFieldByColumnId),
    defaultPageSize: 10,
  },
  [DocumentsTab.Templates]: {
    chipGroups: documentChipGroups(DocumentsTab.Templates),
    columnFilterParameters: { name: "name" },
    sortableColumnIds: Object.keys(orderFieldByColumnId),
    defaultPageSize: 10,
  },
};

/** How a row's Type names each category it is in. */
const typeLabelByCategory: Readonly<Record<DocumentCategory, string>> = {
  [DocumentCategory.Policies]: "Policy",
  [DocumentCategory.Conflicts]: "Conflicts",
  [DocumentCategory.Proposals]: "Proposal",
  [DocumentCategory.Work]: "Work",
  [DocumentCategory.Chat]: "Chat",
  [DocumentCategory.KnowledgeBase]: "Knowledge base",
};

/** Where the columns go when the pane is short of room (#6711): Used in and Size onto the name. */
const documentColumnFolds = {
  usedIn: { into: "name", priority: 1 },
  fileSize: { into: "name", priority: 2 },
} as const;

/** The kinds of document this page uploads. Everything else reaches the list from where it is used. */
type UploadKind = DocumentCategory.Conflicts | DocumentCategory.Policies;

type DocumentAction = "archiving" | "deleting" | "unarchiving";

function formatDocumentCount(count: number): string {
  return `${count} ${count === 1 ? "document" : "documents"}`;
}

// A template on this page is one document, so several files make several templates, never one
// template holding them all. Grouping documents is what a proposal template is for (#6510).
function templateUploadPrompt(kind: UploadKind, fileCount: number) {
  const named = kind === DocumentCategory.Conflicts ? "conflicts" : "policy";
  if (fileCount === 1) {
    return {
      title: "Save as a template?",
      message: `Save this document as a ${named} template, or just upload it?`,
      okButtonText: "Save template",
      alternativeOkText: "Just upload",
      cancelButtonText: "Cancel",
    };
  }
  return {
    title: "Save as templates?",
    message: `You're uploading ${fileCount} documents. Save each one as its own ${named} template, or just upload them?`,
    okButtonText: `Save ${fileCount} templates`,
    alternativeOkText: "Just upload",
    cancelButtonText: "Cancel",
  };
}

function getUploadContext(kind: UploadKind, isTemplate: boolean): string {
  if (isTemplate) {
    return kind === DocumentCategory.Conflicts ? "ConflictsTemplate" : "PolicyTemplate";
  }
  return kind === DocumentCategory.Conflicts ? "Conflicts" : "Policy";
}

/** The one kind the chips say an upload is, when they choose exactly Policies or exactly Conflicts. */
function chosenUploadKind(selection: ListChipSelection): UploadKind | undefined {
  const categories = chosenCategories(selection);
  if (categories.length !== 1) return undefined;
  const [category] = categories;
  return category === DocumentCategory.Conflicts || category === DocumentCategory.Policies ? category : undefined;
}

/** A row's Type: each category it is listed under, as the chips name them. */
function documentTypeOf(row: Document | UnavailableDocument): string {
  if ("issue" in row) return "";
  return documentCategoriesOf(row.topics).map((category) => typeLabelByCategory[category]).join(", ");
}

/** The list's filter: the tab, its chips, the name filter and the search. */
function documentsFilterParameters(
  tab: DocumentsTab,
  chips: ListChipSelection,
  name: string | undefined,
  query: string,
): DocumentFilterParameters {
  return new DocumentFilterParameters({ name, ...documentListFilter(tab, chips), query: query || undefined });
}

function documentUsedIn(row: Document | UnavailableDocument): string | undefined {
  return row instanceof Document ? documentUsagesText(row.topics) : undefined;
}

function documentSize(row: Document | UnavailableDocument): string | undefined {
  return row instanceof Document ? filesize(row.fileSize ?? 0) : undefined;
}

async function runWithGuaranteedCleanup(
  action: () => Promise<unknown>,
  onSuccess: () => void,
  onError: (error: unknown) => void,
  cleanup: () => void,
): Promise<void> {
  try {
    await action();
    onSuccess();
  } catch (error) {
    onError(error);
  } finally {
    cleanup();
  }
}

/**
 * Every document the reader may read, and the templates, in one list with chips (#6720). It was the
 * Contracts page, which listed policies and conflicts only.
 */
export default function DocumentsPage() {
  // Refs
  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);
  // What the file picker is about to upload: the kind, and whether the reader already said template.
  const pendingUploadRef = React.useRef<{ kind: UploadKind; isTemplate: boolean } | null>(null);

  // Route
  const routeParams = useParams();
  const activeTab = readDocumentsTab(routeParams.pageTab) ?? DocumentsTab.Documents;
  const isTemplatesTab = activeTab === DocumentsTab.Templates;

  // State
  const [uploading, setUploading] = React.useState(false);
  const [documentAction, setDocumentAction] = React.useState<DocumentAction | null>(null);
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});
  // The id and not the document, so the dialog always gets the latest read of it - after a 409
  // the list is read again, and the form has to show what that read says (#4030).
  const [subjectMattersDocumentId, setSubjectMattersDocumentId] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [userColumnVisibility, setUserColumnVisibility] = usePersistedColumnVisibility("documents");
  // The view the reader left the page in follows them to a bare visit and to another browser (#6714).
  const location = useLocation();
  const tableViews = useCurrentTableView(DOCUMENTS_CURRENT_VIEW);
  const { remember } = tableViews;
  const { chips, setChips, columnFilters, setColumnFilters, sorting, setSorting, pageSize, setPageSize } =
    useTableViewParams(DOCUMENTS_VIEW[activeTab], {
      onReaderChange: (next) => remember(`${location.pathname}?${next.toString()}`),
    });
  // The page number is not part of a view, so it stays here; the page size is in the URL.
  const [pageIndex, setPageIndex] = useState(0);
  const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);
  function setPagination(updater: MRT_Updater<PaginationState>) {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPageIndex(next.pageIndex);
    if (next.pageSize !== pageSize) setPageSize(next.pageSize);
  }

  // Hooks
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  // From `xl` up the pane spares a column, and the categories are folders beside the list (#6720).
  const showsCategoryRail = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));
  const dispatch = useAttorneyHubDispatch();
  const [createDocuments] = useCreateDocumentsMutation();
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [archiveDocument] = useArchiveDocumentMutation();
  const [unarchiveDocument] = useUnarchiveDocumentMutation();

  // Chips
  const isArchive = listsArchive(chips);
  // What the rows are, for the search box and the empty table: "archived policies", "templates".
  const tableSubject = documentListSubject(activeTab, chips);
  // The Type column says which category a row is in wherever the rows can be of more than one (#5780).
  const rowsMixKinds = rowsMixCategories(chips);
  // A category or owner chip narrows the list, and Clear widens it again; Archived replaces it.
  const narrowsByChips = (chips[CATEGORY_PARAMETER] ?? []).length > 0 || (chips[OWNER_PARAMETER] ?? []).length > 0;
  const uploadKind = chosenUploadKind(chips);
  const selectionViewKey = `${activeTab}|${JSON.stringify(chips)}|${pagination.pageIndex}|${pagination.pageSize}|${globalFilter}|${JSON.stringify(columnFilters)}|${JSON.stringify(sorting)}`;
  const previousSelectionViewKeyRef = React.useRef(selectionViewKey);

  // Queries
  // No page-level gate: every row is one the server let this reader read (`WithUserPermissionsAsync`)
  // and that no proposal field hides from them (`IProposalDocumentFieldAuthorizer`), so a reader with
  // no grants sees an empty list rather than a refusal (#6720).
  const documentAccess = useWhoHasAccess("documents", "the documents");

  const interfaceName = "documentsIndex";
  const documentQuery = useSelector(getDocumentsQueryByInterface(interfaceName));
  const documentsQuery = useGetDocumentsQuery(documentQuery ? { query: documentQuery } : skipToken);
  const documents = documentsQuery.data;
  const isLoading = documentsQuery.isLoading;
  const errorLoadingQuery = documentsQuery.isError;
  const showAlertBanner = Boolean(errorLoadingQuery) || uploading || documentAction !== null;
  const loadingAlertText = uploading
    ? "Uploading..."
    : documentAction === "archiving"
      ? "Archiving..."
      : documentAction === "deleting"
        ? "Deleting..."
        : "Unarchiving...";
  const showProgressBars = isLoading;
  const showSkeletons = isLoading && !documents;

  useEffect(() => {
    if (previousSelectionViewKeyRef.current === selectionViewKey) return;
    previousSelectionViewKeyRef.current = selectionViewKey;
    setRowSelection({});
  }, [selectionViewKey]);

  const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
  // The counts are asked under the list's own filter; the server sets each group's own chips aside
  // when it counts that group.
  const countsCriteria = documentsFilterParameters(activeTab, chips, nameFilter, globalFilter).searchParams.toString();
  const listCounts = useDocumentListCounts({ criteria: countsCriteria }, { skip: !session.user?.legalEntityId });
  const chipCounts = useMemo(() => documentChipCounts(listCounts.counts), [listCounts.counts]);
  const pageTabs = useMemo<readonly ListPageTab<DocumentsTab>[]>(
    () => [
      {
        value: DocumentsTab.Documents,
        label: "Documents",
        counts: "document(s)",
        count: listCounts.counts?.tabs.documents,
        isLoading: listCounts.isLoading,
        badgeColor: "info",
      },
      {
        value: DocumentsTab.Templates,
        label: "Templates",
        counts: "template(s)",
        count: listCounts.counts?.tabs.templates,
        isLoading: listCounts.isLoading,
        badgeColor: "info",
      },
    ],
    [listCounts.counts?.tabs.documents, listCounts.counts?.tabs.templates, listCounts.isLoading],
  );

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const orderingSort = sorting.find((sort) => orderFieldByColumnId[sort.id] !== undefined);
    const newQuery = new DocumentQuery({
      orderParameters: new DocumentOrderParameters({
        orderBy: (orderingSort && orderFieldByColumnId[orderingSort.id]) ?? DocumentOrderField.Created,
        orderDir: orderingSort?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
      }),
      filterParameters: documentsFilterParameters(activeTab, chips, nameFilter, globalFilter),
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });

    if (newQuery.cacheKey === documentQuery?.cacheKey) return;

    dispatch(
      updateDocumentsInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [activeTab, chips, nameFilter, globalFilter, pagination, sorting, session.user?.legalEntityId, documentQuery, dispatch]);

  const columnVisibility = useMemo(() => ({
    ...userColumnVisibility,
    type: rowsMixKinds,
  }), [rowsMixKinds, userColumnVisibility]);

  const handleTabChange = useCallback(
    (tab: DocumentsTab) => {
      // A tab is a different list, so neither its chips nor its filters come from the other one.
      const path = documentsTabPath(tab);
      remember(path);
      setPageIndex(0);
      navigate(path);
    },
    [navigate, remember],
  );

  const chipGroups = documentChipGroups(activeTab);
  const categoryGroup = chipGroups.find((group) => group.parameter === CATEGORY_PARAMETER)!;

  function handleChipsChange(selection: ListChipSelection) {
    setPageIndex(0);
    setChips(selection);
  }

  const handleUploadError = (error: unknown) => {
    const status = getApiErrorStatus(error);
    const errorMessage = getApiErrorMessage(getApiErrorData(error));
    if (status !== undefined) {
      if (status === HttpStatusCode.Forbidden) {
        enqueueSnackbar(errorMessage ?? "Unknown error occurred during upload", { variant: "error" });
        return;
      }
      if (status === HttpStatusCode.UnprocessableEntity || status === HttpStatusCode.UnsupportedMediaType) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
        return;
      }
    }
    enqueueSnackbar("Unknown error occurred during upload", {
      variant: "error",
    });
  };

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const files = Array.from(event.currentTarget.files);
    const pending = pendingUploadRef.current;
    if (files.length === 0 || !pending) {
      event.target.value = "";
      return;
    }

    let isTemplate = pending.isTemplate;
    if (!isTemplate) {
      const templateResponse = await confirm(templateUploadPrompt(pending.kind, files.length));

      if (templateResponse === ConfirmResponse.Cancel) {
        pendingUploadRef.current = null;
        event.target.value = "";
        return;
      }

      isTemplate = templateResponse === ConfirmResponse.Ok;
    }
    const context = getUploadContext(pending.kind, isTemplate);

    const topic = DocumentTopic.fromJSON({ context });
    const requests = createBulkDocumentRequests(files, {
      topics: topic ? [topic] : undefined,
    });
    setUploading(true);
    // The cleanup runs after the try rather than in a `finally`, which the React Compiler cannot lower.
    try {
      const results = await createDocuments({ requests }).unwrap();
      const snackbar = getDocumentUploadSnackbar(summarizeDocumentUploadResults(results, requests));
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
    } catch (error) {
      handleUploadError(error);
    }
    pendingUploadRef.current = null;
    event.target.value = "";
    setUploading(false);
  }

  /** Asks which kind of document the reader is adding, unless one category chip already says. */
  async function chooseUploadKind(isTemplate: boolean): Promise<UploadKind | undefined> {
    if (uploadKind) return uploadKind;
    const response = await confirm(
      isTemplate
        ? {
            title: "Create Template",
            message: "What kind of template do you want to create?",
            okButtonText: "Conflicts Template",
            alternativeOkText: "Policy Template",
            cancelButtonText: "Cancel",
          }
        : {
            title: "Upload",
            message: "What kind of document do you want to upload?",
            okButtonText: "Conflicts",
            alternativeOkText: "Policy",
            cancelButtonText: "Cancel",
          },
    );
    if (response === ConfirmResponse.Cancel) return undefined;
    return response === ConfirmResponse.Ok ? DocumentCategory.Conflicts : DocumentCategory.Policies;
  }

  async function handleCreateTemplateClicked(): Promise<void> {
    const kind = await chooseUploadKind(true);
    if (!kind) return;
    pendingUploadRef.current = { kind, isTemplate: true };
    documentFileUploaderRef.current?.click();
  }

  async function handleUploadClicked(): Promise<void> {
    const kind = await chooseUploadKind(false);
    if (!kind) return;
    pendingUploadRef.current = { kind, isTemplate: false };
    documentFileUploaderRef.current?.click();
  }

  /**
   * Downloads the document file with the given id
   * @param {Guid} id The id of the document file to download
   * @returns {Promise<void>}
   */
  const handleDownload = useCallback(
    async (id: Guid): Promise<void> => {
      try {
        if (!id) {
          await Promise.reject(new Error("No document id provided"));
          return;
        }
        const response = await downloadDocument({ documentId: id.value }).unwrap();
        await downloadFile(response);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(documentDownloadFailureMessage(error, "Failed to download file"), { variant: "error" });
      }
    },
    [downloadDocument],
  );

  /**
   * Deletes the document file with the given id
   * @param {Guid} id The id of the document file to delete
   * @returns {Promise<void>}
   */
  async function handleDelete(id: Guid): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No document id provided"));
      return;
    }

    const response = await confirm({
      title: "Delete Document?",
      message: "Are you sure you want to delete this document?",
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteDocument({ documentId: id.value }).unwrap();
      dispatch(cleanupProposalDeletedDocumentReferences(id));
      dispatch(cleanupWorkAgreementDeletedDocumentReferences(id));
      dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
      dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
      enqueueSnackbar("Document deleted", { variant: "success" });
    } catch (error) {
      if (getApiErrorStatus(error) === HttpStatusCode.Conflict) {
        await documentsQuery.refetch();
        enqueueSnackbar(
          "We could not delete this document because active work uses it. Remove the document from that work and try again.",
          { variant: "error" },
        );
      } else {
        console.error(error);
        enqueueSnackbar("Failed to delete file", { variant: "error" });
      }
    }
  }

  async function handleArchive(id: Guid): Promise<void> {
    if (!id) {
      throw new Error("No document id provided");
    }

    const response = await confirm({
      title: "Archive Document?",
      message: "Are you sure you want to archive this document? This will clear the file from this list, and you can still view archived documents with the Archived chip.",
      okButtonText: "Archive",
    });

    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction("archiving");
    await runWithGuaranteedCleanup(
      () => archiveDocument({ documentId: id.value }).unwrap(),
      () => enqueueSnackbar("Document archived", { variant: "success" }),
      (error) => {
        console.error(error);
        enqueueSnackbar("Failed to archive file", { variant: "error" });
      },
      () => setDocumentAction(null),
    );
  }

  async function handleUnarchive(id: Guid): Promise<void> {
    if (!id) {
      throw new Error("No document id provided");
    }

    const response = await confirm({
      title: "Unarchive Document?",
      message: "Are you sure you want to unarchive this document? This will clear the file from this list.",
      okButtonText: "Unarchive",
    });

    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction("unarchiving");
    await runWithGuaranteedCleanup(
      () => unarchiveDocument({ documentId: id.value }).unwrap(),
      () => enqueueSnackbar("Document unarchived", { variant: "success" }),
      (error) => {
        console.error(error);
        enqueueSnackbar("Failed to unarchive file", { variant: "error" });
      },
      () => setDocumentAction(null),
    );
  }

  const renderNameColumn = useCallback(
    (document: Document | UnavailableDocument, table: MRT_TableInstance<Document | UnavailableDocument>): React.ReactNode => {
      if ("issue" in document) return <span>Unavailable Document</span>;

      const fileType = enumKeyOf(DocumentFileType, document?.fileType?.toString()).toLocaleLowerCase();

      return (
        <NameContainer data-testid="document-name-cell">
          <FileIcon extension={fileType} {...defaultStyles[fileType as keyof typeof defaultStyles]} />
          <NameText>
            {/* The second line carries Used in and Size when the pane is short of room for their columns. */}
            <CondensedCell
              title={
                <DocumentDownloadName
                  document={document}
                  renderLink={(name) => <RowLink onClick={() => handleDownload(document.id)}>{name}</RowLink>}
                >
                  <SearchMatch table={table} text={document.name?.value} />
                </DocumentDownloadName>
              }
            />
            {/*
              Under the name rather than in a column of its own. It is empty for every document
              that finished processing, which is nearly all of them, so a column would be mostly
              whitespace - and the reader is already looking here when they wonder why a document
              they just uploaded is not answerable.
            */}
            <DocumentProcessingState status={document.classificationStatus} scanStatus={document.scanStatus} />
            <SubjectMatterLine>
              <DocumentSubjectMatters document={document} />
            </SubjectMatterLine>
          </NameText>
        </NameContainer>
      );
    },
    [handleDownload]
  );

  const columns = useMemo(
    () => [
      // The server searches a document's name and its topics' contexts (#6590). The name is the
      // value, and the cell draws it, so the cell can mark what the search matched.
      columnHelper.accessor((document) => ("issue" in document ? "" : (document.name?.value ?? "")), {
        header: "Name",
        id: "name",
        meta: { kind: "name" },
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row, table }) => renderNameColumn(row.original, table),
      }),
      columnHelper.accessor(documentTypeOf, {
        header: "Type",
        id: "type",
        meta: { kind: "text" },
        Cell: ({ cell }) => cell.getValue() || <NoValueDash />,
        // Worked out from the topics, as `GET documents` matches the category chips, but a search
        // that matched a topic's context did not match this word (#6590).
        enableGlobalFilter: false,
        // `GET documents` can neither order nor filter by it, so a sort or filter on this column never
        // reached the request (#5454). The category chips are the type filter.
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: rowsMixKinds,
      }),
      columnHelper.accessor((document) => documentUsedIn(document) ?? "", {
        header: "Used in",
        id: "usedIn",
        meta: {
          kind: "text",
          fold: { ...documentColumnFolds.usedIn, render: (row) => { const usedIn = documentUsedIn(row); return usedIn && `Used in ${usedIn}`; } },
        },
        Cell: ({ cell }) => cell.getValue() || <NoValueDash />,
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor((document) => documentSize(document) ?? "", {
        header: "Size",
        id: "fileSize",
        meta: { kind: "number", fold: { ...documentColumnFolds.fileSize, render: documentSize } },
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
      }),
      columnHelper.accessor(
        (document) => {
          if ("issue" in document) return undefined;
          return document?.created?.valueOf();
        },
        {
          header: "Created",
          id: "created",
          meta: { kind: "timestamp" },
          Cell: ({ row }) => <TimestampCell value={"issue" in row.original ? undefined : row.original.created} />,
          enableGlobalFilter: false,
          enableColumnFilter: false,
        },
      ),
    ],
    [renderNameColumn, rowsMixKinds]
  );

  const tableData = useMemo(() => documents?.data ?? [], [documents]);
  const subjectMattersDocument = subjectMattersDocumentId
    ? tableData.find((document): document is Document =>
        document instanceof Document && document.id.value === subjectMattersDocumentId)
    : undefined;

  const getArchiveDisabledReason = useCallback((document: Document | UnavailableDocument): string | undefined => {
    if ("issue" in document) return "Document is unavailable";
    if (document.hasSystemPermissions) return "System document";
    if (document.inUse) return "Document is in use";
    return undefined;
  }, []);

  const getDeleteDisabledReason = useCallback((document: Document | UnavailableDocument): string | undefined => {
    if ("issue" in document) return "Document is unavailable";
    if (document.hasSystemPermissions) return "System document";
    if (document.inUse) return "Document is in use";
    if (!document.creatorId || !session.user?.id || !document.creatorId.isEqualTo(session.user.id)) {
      return "You are not the file owner";
    }
    return undefined;
  }, [session.user]);

  const getUnarchiveDisabledReason = useCallback(
    (document: Document | UnavailableDocument): string | undefined => {
      if ("issue" in document) return "Document is unavailable";
      if (document.hasSystemPermissions) return "System document";
      return undefined;
    },
    [],
  );

  const isBulkSelectable = useCallback(
    (document: Document | UnavailableDocument): boolean => {
      if ("issue" in document || document.hasSystemPermissions) return false;
      return isArchive || !document.inUse;
    },
    [isArchive],
  );

  const selectedDocuments = useMemo(
    () =>
      tableData.filter(
        (document): document is Document =>
          !("issue" in document) && Boolean(rowSelection[document.id.value]),
      ),
    [rowSelection, tableData],
  );

  const deleteDisabledReason = selectedDocuments
    .map(getDeleteDisabledReason)
    .find((reason) => reason !== undefined);
  const archiveDisabledReason = selectedDocuments
    .map(getArchiveDisabledReason)
    .find((reason) => reason !== undefined);
  const unarchiveDisabledReason = selectedDocuments
    .map(getUnarchiveDisabledReason)
    .find((reason) => reason !== undefined);
  const actionInProgressReason = documentAction ? "Another document action is in progress" : undefined;

  async function handleBulkAction(action: "archive" | "delete" | "unarchive"): Promise<void> {
    const selected = [...selectedDocuments];
    if (selected.length === 0 || documentAction) return;

    const eligibilityReason = action === "delete"
      ? deleteDisabledReason
      : action === "archive"
        ? archiveDisabledReason
        : unarchiveDisabledReason;
    if (eligibilityReason) return;

    const actionLabel = action === "unarchive" ? "Unarchive" : `${action[0].toUpperCase()}${action.slice(1)}`;
    const response = await confirm({
      title: `${actionLabel} Documents?`,
      message: `Are you sure you want to ${action} ${formatDocumentCount(selected.length)}?`,
      okButtonText: actionLabel,
    });
    if (response === ConfirmResponse.Cancel) return;

    setDocumentAction(action === "archive" ? "archiving" : action === "delete" ? "deleting" : "unarchiving");
    const results = await Promise.allSettled(
      selected.map((document) =>
        Promise.resolve().then(() => {
          if (action === "archive") return archiveDocument({ documentId: document.id.value }).unwrap();
          if (action === "unarchive") return unarchiveDocument({ documentId: document.id.value }).unwrap();
          return deleteDocument({ documentId: document.id.value }).unwrap();
        }),
      ),
    );

    const succeededDocuments = selected.filter((_document, index) => results[index].status === "fulfilled");
    const failedDocuments = selected.filter((_document, index) => results[index].status === "rejected");
    results.forEach((result) => {
      if (result.status === "rejected") console.error(result.reason);
    });

    if (action === "delete" && succeededDocuments.length > 0) {
      succeededDocuments.forEach((document) => {
        dispatch(cleanupProposalDeletedDocumentReferences(document.id));
        dispatch(cleanupWorkAgreementDeletedDocumentReferences(document.id));
      });
      dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
      dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
    }

    const hasVisibleUseConflict = action === "delete" && results.some(
      (result) => result.status === "rejected" && getApiErrorStatus(result.reason) === HttpStatusCode.Conflict,
    );
    if (hasVisibleUseConflict) {
      await documentsQuery.refetch();
    }

    setRowSelection(Object.fromEntries(failedDocuments.map((document) => [document.id.value, true])));
    const pastTenseAction = action === "archive" ? "archived" : action === "delete" ? "deleted" : "unarchived";
    if (hasVisibleUseConflict) {
      enqueueSnackbar(
        "We could not delete this document because active work uses it. Remove the document from that work and try again.",
        { variant: succeededDocuments.length > 0 ? "warning" : "error" },
      );
    } else if (failedDocuments.length === 0) {
      enqueueSnackbar(`${formatDocumentCount(succeededDocuments.length)} ${pastTenseAction}`, { variant: "success" });
    } else if (succeededDocuments.length === 0) {
      enqueueSnackbar(`Failed to ${action} ${formatDocumentCount(failedDocuments.length)}`, { variant: "error" });
    } else {
      enqueueSnackbar(
        `${formatDocumentCount(succeededDocuments.length)} ${pastTenseAction}; ${formatDocumentCount(failedDocuments.length)} failed`,
        { variant: "warning" },
      );
    }
    setDocumentAction(null);
  }

  const renderBulkActionButton = (
    action: "archive" | "delete" | "unarchive",
    disabledReason: string | undefined,
  ): React.ReactNode => {
    const label = action === "archive" ? "Archive" : action === "delete" ? "Delete" : "Unarchive";
    const icon = action === "archive" ? <ArchiveIcon /> : action === "delete" ? <DeleteIcon /> : <UnarchiveIcon />;
    const effectiveDisabledReason = actionInProgressReason ?? disabledReason;
    const button = isMediumDisplaySize ? (
      <IconButton
        aria-label={label}
        color={action === "delete" ? "error" : "inherit"}
        disabled={Boolean(effectiveDisabledReason)}
        onClick={() => void handleBulkAction(action)}
      >
        {icon}
      </IconButton>
    ) : (
      <Button
        color={action === "delete" ? "error" : "inherit"}
        disabled={Boolean(effectiveDisabledReason)}
        startIcon={icon}
        onClick={() => void handleBulkAction(action)}
      >
        {label}
      </Button>
    );

    return (
      <Tooltip key={action} title={effectiveDisabledReason ? `Unable to ${action}: ${effectiveDisabledReason}` : label}>
        <span>{button}</span>
      </Tooltip>
    );
  };

  const bulkActionButtons = selectedDocuments.length > 0 ? (
    <AlertActions>
      {isArchive
        ? renderBulkActionButton("unarchive", unarchiveDisabledReason)
        : renderBulkActionButton("archive", archiveDisabledReason)}
      {renderBulkActionButton("delete", deleteDisabledReason)}
    </AlertActions>
  ) : undefined;

  // Templates are created on Templates and documents uploaded on Documents; the archive takes neither.
  const createAction = isArchive
    ? undefined
    : isTemplatesTab
      ? { label: "Create Template", icon: <AddBoxIcon />, largeIcon: <AddBoxIcon fontSize="large" />, onClick: handleCreateTemplateClicked }
      : { label: "Upload", icon: <UploadIcon />, largeIcon: <UploadIcon fontSize="large" />, onClick: handleUploadClicked };

  const tableOptions: MRT_TableOptions<Document | UnavailableDocument> = {
    columns,
    data: tableData,
    enableRowSelection: (row) => documentAction === null && isBulkSelectable(row.original),
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: tableSubject, table }),
    // The chips are the page's own filter and the table knows nothing about them, so without
    // `hasPageFilters` a chip that excludes everything reads as an empty account: "No policies"
    // where the truth is "no policies match". Clear turns them off. Archived alone replaces the list
    // rather than narrowing it, so an empty archive is "No archived documents".
    renderEmptyRowsFallback: emptyRowsFallback({
      hasPageFilters: narrowsByChips,
      isError: errorLoadingQuery,
      isLoading: documentsQuery.isFetching,
      onRetry: () => void documentsQuery.refetch(),
      subject: tableSubject,
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ListPageToolbarButtons>
            <ToolbarButtons>
              <ViewsMenu views={tableViews} table={table} />
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
            {createAction && (
              <TableActionLoadingButton loading={uploading} startIcon={createAction.icon} onClick={createAction.onClick}>
                {createAction.label}
              </TableActionLoadingButton>
            )}
          </ListPageToolbarButtons>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <ListPageHeading
        title="Documents"
        titleAction={
          isMediumDisplaySize && createAction ? (
            <TableActionIconButton aria-label={createAction.label} color="primary" onClick={createAction.onClick}>
              {createAction.largeIcon}
            </TableActionIconButton>
          ) : undefined
        }
        tabs={<ListPageTabs value={activeTab} tabs={pageTabs} onChange={(_event, tab) => handleTabChange(tab)} />}
        chips={
          <ListChips
            groups={chipGroups}
            selection={chips}
            counts={chipCounts}
            onChange={handleChipsChange}
            drawnElsewhere={showsCategoryRail ? [CATEGORY_PARAMETER] : undefined}
          />
        }
        mobileToolbar={
          isMediumDisplaySize ? (
            <ListPageMobileToolbar>
              <ViewsMenu views={tableViews} table={table} />
              <ListPageMobileSearch table={table} />
              <TableToolsMenu table={table} />
            </ListPageMobileToolbar>
          ) : undefined
        }
        criteria={<ColumnFilterChips table={table} />}
      />
    ),
    renderRowActions: ({ row }) => {
      const deleteDisabledReason = getDeleteDisabledReason(row.original);
      const archiveDisabledReason = getArchiveDisabledReason(row.original);
      const isDeleteDisabled = Boolean(deleteDisabledReason) || documentAction !== null;
      const isArchiveDisabled = Boolean(archiveDisabledReason) || documentAction !== null;
      const isUnarchiveDisabled = Boolean(getUnarchiveDisabledReason(row.original)) || documentAction !== null;
      return (
        <RowActions
          ariaLabel={`Actions for ${row.original instanceof UnavailableDocument ? "document" : row.original.name?.value ?? "document"}`}
          // Delete is the one icon; everything else waits in the menu beside it. A row can carry
          // five actions, and five icons was more column than the table could spare. Download
          // gives up least by moving, because the document's name is already the download.
          actions={[
            documentDownloadRowAction(row.original instanceof Document ? row.original : undefined, {
              inMenu: true,
              onClick: () => handleDownload(row.original.id),
            }),
            // Offered only where the API says the override would be accepted. A control that
            // opens a form and then fails with 403 is worse than no control (#4030).
            ...(row.original instanceof Document && row.original.canOverrideClassification
              ? [{
                  key: "edit-subject-matter",
                  label: "Edit subject matter",
                  icon: <LabelIcon />,
                  inMenu: true,
                  onClick: () => setSubjectMattersDocumentId(row.original.id.value),
                }]
              : []),
            // Who holds a grant on the document, for an administrator with Member Permissions (#6172).
            // Offered for a document the reader cannot open as well: its holders are still listed.
            ...(documentAccess.mayOpen
              ? [{
                  key: "who-has-access",
                  label: recordPage.whoHasAccess,
                  // The lock every record shows for Who has access (#6598).
                  icon: <LockOutlinedIcon />,
                  inMenu: true,
                  onClick: () => documentAccess.open(row.original.id),
                }]
              : []),
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => handleDelete(row.original.id),
              disabled: isDeleteDisabled,
              disabledReason: deleteDisabledReason ? `Unable to delete: ${deleteDisabledReason}` : undefined,
            },
            isArchive
              ? {
                  key: "unarchive",
                  label: "Unarchive",
                  icon: <UnarchiveIcon />,
                  inMenu: true,
                  onClick: () => handleUnarchive(row.original.id),
                  disabled: isUnarchiveDisabled,
                  disabledReason: isUnarchiveDisabled ? "Unable to unarchive" : undefined,
                }
              : {
                  key: "archive",
                  label: "Archive",
                  icon: <ArchiveIcon />,
                  inMenu: true,
                  onClick: () => handleArchive(row.original.id),
                  disabled: isArchiveDisabled,
                  disabledReason: archiveDisabledReason ? `Unable to archive: ${archiveDisabledReason}` : undefined,
                },
          ]}
        />
      );
    },
    getRowId: (row, index) => {
      if (row instanceof UnavailableDocument) return `unavailable-document-${row.id}`;
      // MRT's loading placeholders carry no id, and one key for all of them is a duplicate React key.
      return row.id?.value ?? `placeholder-${index}`;
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: {
      action: bulkActionButtons,
      color: errorLoadingQuery ? "error" : "info",
      children: showAlertBanner
        ? errorLoadingQuery
          ? "Error loading data"
          : <UploadingLoader size={24} text={loadingAlertText} />
        : undefined,
      sx: (theme) => ({
        "& .MuiAlert-message": {
          minWidth: 0,
          width: "auto",
        },
        "& .MuiAlert-action": {
          alignItems: "center",
          alignSelf: "stretch",
          backgroundColor: "inherit",
          marginRight: 0,
          paddingRight: theme.spacing(1),
          position: "sticky",
          right: 0,
          zIndex: 1,
        },
      }),
    },
    rowCount: documents?.totalElements ?? 0,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setUserColumnVisibility,
    state: {
      columnVisibility,
      columnFilters,
      globalFilter,
      isLoading: isLoading,
      pagination,
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  };

  const table = (
    <MRTNoMemo<Document | UnavailableDocument>
      options={tableOptions}
      columnChoice={userColumnVisibility}
      tableKey="documents"
      cards
    />
  );
  return (
    <>
      {showsCategoryRail ? (
        <RailAndList elevation={2}>
          <ListChipRail
            group={categoryGroup}
            groups={chipGroups}
            selection={chips}
            onChange={handleChipsChange}
            allLabel={activeTab === DocumentsTab.Templates ? "All templates" : "All documents"}
            allCount={(chips[CATEGORY_PARAMETER] ?? []).length === 0 ? listCounts.counts?.total : undefined}
            counts={chipCounts}
          />
          {table}
        </RailAndList>
      ) : (
        table
      )}
      {subjectMattersDocument && (
        <EditSubjectMattersDialog
          key={subjectMattersDocument.id.value}
          document={subjectMattersDocument}
          onClose={() => setSubjectMattersDocumentId(null)}
        />
      )}
      {documentAccess.drawer}
      <input type="file" hidden={true} multiple={true} ref={documentFileUploaderRef} onChange={handleDocumentFileUpload} />
    </>
  );
}
